import React, { useState, useEffect, useCallback, useRef } from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container as ContainerBase } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import logo from "images/lra_pink.svg";
import { ReactComponent as CloseIcon } from "feather-icons/dist/icons/x.svg";
import { ReactComponent as MailIcon } from "feather-icons/dist/icons/send.svg";
import ReactModalAdapter from "../helpers/ReactModalAdapter.js";
import { PrimaryButton } from "../components/misc/Buttons.js";
import { createClient } from "supabase/client.ts";
import { useAuth } from "hooks/auth.js";
import { api } from "supabase/database/api.ts";
import { ReactComponent as UploadIcon } from "feather-icons/dist/icons/upload.svg";
import { ReactComponent as XIcon } from "feather-icons/dist/icons/x.svg";
import { ReactComponent as HomeIcon } from "feather-icons/dist/icons/home.svg";
import { useNavigate } from "react-router-dom";

const Container = tw(
  ContainerBase
)`min-h-screen bg-primary-900 text-white font-medium flex justify-center -m-8`;
const Content = tw.div`max-w-screen-xl m-0 sm:mx-20 sm:my-16 bg-white text-gray-900 shadow sm:rounded-lg flex justify-center flex-1`;
const MainContainer = tw.div`lg:w-2/3 p-6 sm:p-12`;
const LogoLink = tw.a``;
const LogoImage = tw.img`h-12 mx-auto`;
const MainContent = tw.div`mt-2 flex flex-col items-center`;
const Heading = tw.h2`text-2xl sm:text-4xl font-black tracking-wide text-center text-gray-900`;
const FormContainer = tw.div`w-full tracking-wide flex-1 mt-4`;

const Form = tw.form`mx-auto max-w-[80%]`;
const Input = tw.input`w-full px-8 py-4 mb-4 rounded-lg font-medium bg-gray-100 border border-gray-200 placeholder-gray-500 text-sm focus:outline-none focus:border-gray-400 focus:bg-white mt-5 first:mt-0`;

const FileUploadContainer = styled.div`
  ${tw`w-full border-2 border-dashed border-gray-300 rounded-lg p-6 text-center transition-colors duration-200 hover:border-primary-500`}

  &:hover {
    background-color: rgba(222, 49, 99, 0.05);
  }

  &.dragover {
    ${tw`border-primary-500`}
    background-color: rgba(222, 49, 99, 0.05);
  }
`;

const FileList = styled.div`
  ${tw`mt-4 space-y-2`}
`;

const FileItem = styled.div`
  ${tw`flex items-center justify-between p-3 bg-gray-500 rounded-lg border`}
`;

const FileInfo = styled.div`
  ${tw`flex items-center space-x-3 flex-1 min-w-0`}
`;

const FileName = styled.span`
  ${tw`text-sm font-medium text-gray-900 truncate`}
`;

const FileSize = styled.span`
  ${tw`text-xs text-gray-500`}
`;

const RemoveButton = styled.button`
  ${tw`text-gray-400 hover:text-red-500 transition-colors duration-200 p-1 rounded-full hover:bg-red-500`}
`;

const UploadText = styled.div`
  ${tw`space-y-2`}

  .primary-text {
    ${tw`text-sm font-medium text-gray-900`}
  }

  .secondary-text {
    ${tw`text-xs text-gray-600`}
  }

  .browse-text {
    ${tw`text-primary-500 underline cursor-pointer hover:text-primary-700`}
  }
`;

const CustomDropdown = styled.div`
  ${tw`relative w-full mt-5 first:mt-0`}
`;

const DropdownButton = styled.button`
  ${tw`w-full px-8 py-4 rounded-lg font-medium bg-gray-100 border border-gray-200 text-sm focus:outline-none focus:border-gray-400 focus:bg-white text-left flex items-center justify-between`}

  &:focus {
    ${tw`border-gray-400 bg-white`}
  }
`;

const DropdownList = styled.div`
  ${tw`absolute left-0 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-full overflow-y-auto mt-1`}
  top: 100%;
  max-height: 20rem;
`;

const DropdownOption = styled.div`
  ${tw`px-8 py-3 cursor-pointer hover:bg-primary-500 hover:text-white text-sm`}

  &:first-child {
    ${tw`rounded-t-lg`}
  }

  &:last-child {
    ${tw`rounded-b-lg`}
  }

  &.selected {
    ${tw`bg-primary-100 text-primary-700`}
  }

  .category-name {
    ${tw`font-bold`}
  }

  .category-desc {
    ${tw`font-normal text-gray-600`}
  }

  &:hover .category-desc {
    ${tw`text-white`}
  }
`;

const TextArea = tw.textarea`w-full px-8 py-4 rounded-lg font-medium bg-gray-100 border border-gray-200 placeholder-gray-500 text-sm focus:outline-none focus:border-gray-400 focus:bg-white mt-5 first:mt-0 min-h-32`;
const SubmitButton = styled.button`
  ${tw`mt-5 tracking-wide font-semibold bg-primary-500 text-gray-100 w-full py-4 rounded-lg hover:bg-primary-900 transition-all duration-300 ease-in-out flex items-center justify-center focus:shadow-outline focus:outline-none`}
  .icon {
    ${tw`w-6 h-6 -ml-2`}
  }
  .text {
    ${tw`ml-3`}
  }
`;

const Text = styled.div`
  ${tw`text-sm mt-6 mb-6 text-gray-800`}
  p {
    ${tw`m-2 leading-loose`}
  }
  h1 {
    ${tw`text-3xl font-bold mt-10`}
  }
  h2 {
    ${tw`text-2xl font-bold mt-8`}
  }
  h3 {
    ${tw`text-xl font-bold mt-6`}
  }
  ul {
    ${tw`list-disc list-inside`}
    li {
      ${tw`ml-2 mb-3`}
      p {
        ${tw`mt-0 inline leading-normal`}
      }
    }
  }
`;

const StyledModal = styled(ReactModalAdapter)`
  &.mainHeroModal__overlay {
    ${tw`fixed inset-0 z-50`}
  }
  &.mainHeroModal__content {
    ${tw`xl:mx-auto m-4 sm:m-16 max-w-screen-xl absolute inset-0 flex justify-center items-center rounded-lg bg-gray-200 outline-none`}
  }
  .content {
    ${tw`w-full lg:p-16`}
  }
`;
const CloseModalButton = tw.button`absolute top-0 right-0 mt-8 mr-8 hocus:text-primary-500`;

export default ({
  logoLinkUrl = "#",
  headingText = "Heavenly Inbox",
  submitButtonText = "Submit",
  SubmitButtonIcon = MailIcon,
}) => {
  const supabase = createClient();
  const [category, setCategory] = useState("");
  const [name, setName] = useState("");
  const [duty, setDuty] = useState("");
  const [scjid, setScjIdNumber] = useState("");
  const [region, setRegion] = useState("");
  const [message, setMessage] = useState("");
  const [contact, setContact] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [userData, setUserdata] = useState([]);
  const { user } = useAuth();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [guidesOpen, setGuidesOpen] = useState(true);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [dutyDropdownOpen, setDutyDropdownOpen] = useState(false);
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const dutyDropdownRef = useRef(null);
  const navigate = useNavigate();
  const categoryOptions = [
    {
      value: "Worship Service",
      name: "Worship Service",
      desc: "experience, atmosphere",
    },
    {
      value: "Facilities",
      name: "Facilities",
      desc: "venues, equipment, accessibility",
    },
    { value: "Education & Training", name: "Education & Training", desc: "" },
    {
      value: "Evangelism",
      name: "Evangelism",
      desc: "tools, skills development, process",
    },
    {
      value: "Social / Community Building",
      name: "Social / Community Building",
      desc: "events, fellowship, member support",
    },
    {
      value: "Heavenly Culture Development",
      name: "Heavenly Culture Development",
      desc: "",
    },
    {
      value: "Other",
      name: "Other",
      desc: "any suggestions not captured above",
    },
  ];

  const dutyOptions = [
    { value: "SGN", name: "SGN", desc: "" },
    { value: "SMN", name: "SMN", desc: "" },
    { value: "CMN", name: "CMN", desc: "" },
    { value: "GJN", name: "GJN", desc: "" },
    { value: "JJN", name: "JJN", desc: "" },
    { value: "IWN", name: "IWN", desc: "" },
    { value: "TJN", name: "TJN", desc: "" },
    { value: "GGN", name: "GGN", desc: "" },
    { value: "GYJN", name: "GYJN", desc: "" },
    { value: "BGYJN", name: "BGYJN", desc: "" },
    { value: "JPSN", name: "JPSN", desc: "" },
    { value: "HYJN", name: "HYJN", desc: "" },
    { value: "JMN", name: "JMN", desc: "" },
  ];

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setCategoryDropdownOpen(false);
      }
      if (
        dutyDropdownRef.current &&
        !dutyDropdownRef.current.contains(event.target)
      ) {
        setDutyDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleModal = () => setModalIsOpen(!modalIsOpen);
  const toggleGuidelines = () => setGuidesOpen(!guidesOpen);

  const pullData = useCallback(async () => {
    const userdata = await api.authQueries.getUserInfo({ email: user.email })
    setUserdata(userdata && userdata[0]);
    // Pre-populate region, name, and scjid from user data (hidden fields)
    setRegion(userdata[0]?.region || "");
    setName(userdata[0]?.name || "");
    setScjIdNumber(userdata[0]?.registration_number);
  }, [user.email]);

  useEffect(() => {
    pullData();
  }, [pullData]);

  const handleFileSelect = (files) => {
    const fileArray = Array.from(files);
    console.log("Files selected:", fileArray);

    // Store raw files (not uploaded yet) - upload happens on submit
    setAttachments((prev) => {
      const newAttachments = [...prev, ...fileArray];
      console.log("All selected files:", newAttachments);
      return newAttachments;
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const removeFile = (index) => {
    setAttachments((prev) => {
      const newAttachments = prev.filter((_, i) => i !== index);

      // If no files left, clear the file input
      if (newAttachments.length === 0) {
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        const fileInput = document.getElementById("file-upload");
        if (fileInput) {
          fileInput.value = "";
        }
      }

      return newAttachments;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Regular expression for validating mobile numbers
    const mobileNumberRegex = /^(\+27|0)[6-8][0-9]{8}$/;

    // Sanitize the contact input to remove any non-digit characters except for the leading +
    const sanitizedContact = contact.replace(/[^\d+]/g, "").replace(/\s+/g, "");

    // Form validation can be added here (e.g., check if fields are filled)
    if (!category || !duty || !message) {
      alert("Please fill in all required fields.");
      return;
    }

    // Validate the sanitized contact number only if contact is provided
    if (contact && !mobileNumberRegex.test(sanitizedContact)) {
      alert(
        "Please enter a valid mobile number (e.g., +27 69 245 1101 or 0692451101)."
      );
      return;
    }

    try {
      setUploadingFiles(true);

      // Upload files to storage first (like ToolForm does)
      const uploadedFileNames = [];

      if (attachments.length > 0) {
        for (const file of attachments) {
          try {
            console.log(`Uploading file: ${file.name}`);

            // Upload the file to Supabase Storage (same bucket as ToolForm)
            const { data, error } = await supabase.storage
              .from("assets") // Same bucket as ToolForm
              .upload(file.name, file);

            if (error) {
              console.error("Error uploading file:", error.message);
              alert(`Error uploading ${file.name}: ${error.message}`);
              continue;
            }

            // Extract filename like ToolForm does
            const fileName = data.path.split("/").pop();
            uploadedFileNames.push({
              originalName: file.name,
              fileName: fileName,
              size: file.size,
              type: file.type,
              extension: file.name.split(".").pop().toLowerCase(),
            });

            console.log(`Successfully uploaded: ${file.name} as ${fileName}`);
          } catch (uploadError) {
            console.error(`Error uploading ${file.name}:`, uploadError);
            alert(`Error uploading ${file.name}: ${uploadError.message}`);
          }
        }
      }

      // Create JSON object for attachments (like ToolForm stores flyer_uploaded)
      const attachmentsJson =
        uploadedFileNames.length > 0 ? JSON.stringify(uploadedFileNames) : null;

      console.log("Attachments JSON to be stored:", attachmentsJson);

      // Prepare submission data (always includes name and scj_id from logged-in user)
      const submissionData = {
        category,
        name: name, // Always from logged-in user
        scj_id: scjid, // Always from logged-in user
        region,
        duty,
        message,
        contact: sanitizedContact || null,
        attachments: attachmentsJson,
        date: new Date().toISOString(),
      };

      console.log("Submission data:", submissionData);

      // Submit data to Supabase
      const { data, error } = await supabase
        .from("suggestions") // Make sure to change this to your Supabase table
        .insert([submissionData]);

      console.log("Supabase response:", { data, error });

      if (error) {
        console.error("Error submitting form:", error);
        alert(`Error submitting form: ${error.message}`);
        setUploadingFiles(false);
      } else {
        console.log("Form submitted successfully:", data);
        // Reset form fields and clear uploaded files
        setName(userData.name || ""); // Reset to user's name
        setScjIdNumber(userData.registration_number); // Reset to user's ID
        setRegion(userData.region);
        setDuty("");
        setCategory("");
        setMessage("");
        setContact("");
        setAttachments([]); // Clear selected files
        // Clear the file input element using both methods
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        const fileInput = document.getElementById("file-upload");
        if (fileInput) {
          fileInput.value = "";
        }
        setUploadingFiles(false);
        setModalIsOpen(true); // Show modal on success
      }
    } catch (error) {
      console.error("Error:", error);
      alert(`Unexpected error: ${error.message}`);
      setUploadingFiles(false);
    }
  };

  return (
    <AnimationRevealPage>
      <Container>
        <Content>
          <MainContainer>
            <LogoLink href={logoLinkUrl}>
              <LogoImage src={logo} />
            </LogoLink>
            <MainContent>
              <Heading>{headingText}</Heading>
              <FormContainer>
                <Form onSubmit={handleSubmit}>
                  <Input
                    type="text"
                    placeholder="Group/Ministry - Duty (YG - GYJN)"
                    value={duty}
                    onChange={(e) => setDuty(e.target.value)}
                  />
                  <CustomDropdown ref={dropdownRef}>
                    <DropdownButton
                      type="button"
                      onClick={() =>
                        setCategoryDropdownOpen(!categoryDropdownOpen)
                      }
                      className={!category ? "text-gray-500" : ""}>
                      <span>{category || "Select submission category"}</span>
                      <span
                        className={`transform transition-transform ${
                          categoryDropdownOpen ? "rotate-180" : ""
                        }`}>
                        ▼
                      </span>
                    </DropdownButton>
                    {categoryDropdownOpen && (
                      <DropdownList>
                        {categoryOptions.map((option) => (
                          <DropdownOption
                            key={option.value}
                            onClick={() => {
                              setCategory(option.value);
                              setCategoryDropdownOpen(false);
                            }}>
                            <span className="category-name">{option.name}</span>
                            {option.desc && (
                              <span className="category-desc">
                                {" "}
                                – {option.desc}
                              </span>
                            )}
                          </DropdownOption>
                        ))}
                      </DropdownList>
                    )}
                  </CustomDropdown>
                  <TextArea
                    placeholder="Message / Details"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    required
                  />
                  <Input
                    type="text"
                    placeholder="Contact Information (optional - for feedback or clarification)"
                    value={contact}
                    onChange={(e) => setContact(e.target.value)}
                  />

                  <div className="mt-8 mb-8">
                    <label className="block text-sm font-medium text-gray-700 mb-2 text-center">
                      Attachments (optional – images or supporting files)
                    </label>

                    <FileUploadContainer
                      className={dragOver ? "dragover" : ""}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}>
                      <UploadText>
                        <UploadIcon className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                        <div className="primary-text text-center">
                          <span
                            className="browse-text"
                            onClick={() => fileInputRef.current?.click()}>
                            {uploadingFiles
                              ? "Uploading..."
                              : "Click to upload"}
                          </span>{" "}
                          or drag and drop
                        </div>
                        <div className="secondary-text text-center">
                          PNG, JPG, PDF, DOC up to 10MB each
                          {uploadingFiles && " - Uploading files..."}
                        </div>
                      </UploadText>

                      <input
                        ref={fileInputRef}
                        id="file-upload"
                        type="file"
                        multiple
                        accept="image/*,.pdf,.doc,.docx,.txt"
                        onChange={(e) => handleFileSelect(e.target.files)}
                        className="hidden"
                      />
                    </FileUploadContainer>

                    {attachments.length > 0 && (
                      <FileList>
                        {attachments.map((file, index) => (
                          <FileItem key={index}>
                            <FileInfo>
                              <div className="flex-shrink-0">
                                <div
                                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                                  style={{
                                    backgroundColor: "rgba(222, 49, 99, 0.1)",
                                  }}>
                                  <span className="text-xs font-medium text-primary-500">
                                    {file.name.split(".").pop().toUpperCase()}
                                  </span>
                                </div>
                              </div>
                              <div className="min-w-0 flex-1">
                                <FileName>{file.name}</FileName>
                                <FileSize>
                                  {(file.size / 1024).toFixed(1)} KB
                                </FileSize>
                              </div>
                            </FileInfo>
                            <RemoveButton onClick={() => removeFile(index)}>
                              <XIcon className="h-4 w-4" />
                            </RemoveButton>
                          </FileItem>
                        ))}
                      </FileList>
                    )}
                  </div>
                  <SubmitButton type="submit" disabled={uploadingFiles}>
                    <SubmitButtonIcon className="icon" />
                    <span className="text">{uploadingFiles ? "Submitting..." : submitButtonText}</span>
                  </SubmitButton>

                  <SubmitButton onClick={() => navigate("/home")}>
                    <HomeIcon className="icon" />
                    <span className="text">{"Home"}</span>
                  </SubmitButton>
                  <Text>
                    <p>
                      <strong>Submission Guidelines:</strong>
                    </p>
                    <p>
                      • All information can only be accessed by Southern Africa
                      HQ and will not be shared elsewhere.
                    </p>
                    <p>
                      • Please follow the proper order by inquiring first, and
                      then if the issue remains unresolved, use this suggestion
                      box.
                    </p>
                    <p>
                      • Be specific and constructive in your feedback to help us
                      improve our services and community.
                    </p>
                    <p>
                      • Providing contact information helps us follow up if
                      clarification is needed.
                    </p>
                  </Text>
                </Form>
              </FormContainer>
              <StyledModal
                closeTimeoutMS={300}
                className="mainHeroModal"
                isOpen={modalIsOpen}
                onRequestClose={toggleModal}
                shouldCloseOnOverlayClick={true}>
                <CloseModalButton onClick={toggleModal}>
                  <CloseIcon tw="w-6 h-6" />
                </CloseModalButton>
                <MainContent>
                  <Heading>Submission Successful</Heading>
                  <br />
                  <br />
                  <p tw="text-center">Your comment has been submitted.</p>
                  <PrimaryButton onClick={toggleModal}>Close</PrimaryButton>
                </MainContent>
              </StyledModal>

              {/* Guideline Modal */}
              <StyledModal
                className="mainHeroModal"
                isOpen={guidesOpen}
                onRequestClose={toggleGuidelines}
                shouldCloseOnOverlayClick={true}>
                <CloseModalButton onClick={toggleGuidelines}>
                  <CloseIcon tw="w-6 h-6" />
                </CloseModalButton>
                <MainContent>
                  <Heading>Important Notes</Heading>
                  <Text>
                    <p>
                      • It is created for the development of the church, 
                      to help us better our church community, 
                      and to create heaven as our home.
                    </p>
                    <p>
                      • Please communicate truthfully, 
                      respectfully, and with sincerity.
                    </p>
                    <p>
                      • Know that intentionally false claims may result in 
                      consequences.
                    </p>
                    <p>
                      • Let us build the church together by sharing with faith 
                      and sincerity, as we continue to grow and glorify 
                      God throughout Southern Africa.
                    </p>
                  </Text>
                  <PrimaryButton onClick={toggleGuidelines}>Continue</PrimaryButton>
                </MainContent>
              </StyledModal>
            </MainContent>
          </MainContainer>
        </Content>
      </Container>
    </AnimationRevealPage>
  );
};
